import { useMemo } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";
import { Target as TargetIcon } from "lucide-react";

// Level-based color palette — light, professional, IBM-Plex-friendly
const LEVEL_COLORS = {
  Impact: { border: "#7C2D12", bg: "#FFF7ED", text: "#7C2D12", lane: "Impact" },
  Outcome: { border: "#1E40AF", bg: "#EFF6FF", text: "#1E40AF", lane: "Outcome" },
  Output: { border: "#065F46", bg: "#ECFDF5", text: "#065F46", lane: "Output" },
  Activity: { border: "#475569", bg: "#F8FAFC", text: "#475569", lane: "Activity" },
  Default: { border: "#475569", bg: "#FFFFFF", text: "#334155", lane: "Unclassified" },
};

const LEVEL_ORDER = ["Impact", "Outcome", "Output", "Activity", "Unclassified"];

function levelFor(ind) {
  const t = (ind.tags || []).find((tg) => (tg.key || "").toLowerCase() === "level");
  const raw = (t?.value || "").trim();
  // normalise: "Output" / "output" / "OUTPUTS" → "Output"
  const cap = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase().replace(/s$/, "");
  if (LEVEL_COLORS[cap]) return cap;
  return "Default";
}

// Build a dagre graph to compute node (x,y). Returns {nodes, edges} for react-flow.
function layoutIndicators(indicators, breakdownByIid) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "LR", ranksep: 80, nodesep: 24 });

  const W = 260;
  const H = 96;

  for (const ind of indicators) {
    g.setNode(ind.id, { width: W, height: H });
  }
  const edges = [];
  for (const ind of indicators) {
    if (ind.parent_indicator_id && indicators.find((i) => i.id === ind.parent_indicator_id)) {
      g.setEdge(ind.parent_indicator_id, ind.id);
      edges.push({
        id: `e-${ind.parent_indicator_id}-${ind.id}`,
        source: ind.parent_indicator_id,
        target: ind.id,
        type: "smoothstep",
        animated: false,
        style: { stroke: "#94A3B8", strokeWidth: 1.5 },
      });
    }
  }
  dagre.layout(g);

  const nodes = indicators.map((ind) => {
    const pos = g.node(ind.id);
    return {
      id: ind.id,
      type: "logframe",
      position: { x: pos.x - W / 2, y: pos.y - H / 2 },
      data: { indicator: ind, breakdown: breakdownByIid[ind.id] || null },
    };
  });

  return { nodes, edges };
}

function LogFrameNode({ data }) {
  const ind = data.indicator;
  const br = data.breakdown;
  const level = levelFor(ind);
  const col = LEVEL_COLORS[level];

  let progressPct = null;
  let progressLabel = null;
  const tgt = ind.target;
  if (tgt && tgt.mode === "single" && tgt.value && br?.total != null) {
    const pct = Math.round((br.total / tgt.value) * 100);
    progressPct = Math.max(0, Math.min(100, pct));
    progressLabel = `${br.total.toLocaleString()} / ${tgt.value.toLocaleString()} ${ind.unit || ""}`.trim();
  } else if (br?.total != null && ind.data_type === "numeric") {
    progressLabel = `${br.total.toLocaleString()} ${ind.unit || ""}`.trim();
  }

  return (
    <div
      className="rounded-sm shadow-sm border-l-4 px-3 py-2"
      style={{
        width: 260,
        minHeight: 96,
        background: col.bg,
        borderLeftColor: col.border,
        borderTop: "1px solid #E2E8F0",
        borderRight: "1px solid #E2E8F0",
        borderBottom: "1px solid #E2E8F0",
      }}
      data-testid={`diagram-node-${ind.code}`}
    >
      <Handle type="target" position={Position.Left} style={{ background: col.border, width: 6, height: 6, border: "none" }} />
      <Handle type="source" position={Position.Right} style={{ background: col.border, width: 6, height: 6, border: "none" }} />
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] tracking-wider" style={{ color: col.text }}>{ind.code}</span>
        <span
          className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
          style={{ color: col.border, background: "#FFFFFFCC" }}
        >
          {LEVEL_COLORS[level].lane}
        </span>
      </div>
      <p className="text-[13px] leading-snug text-slate-800 mt-1 line-clamp-2">{ind.name}</p>
      {progressLabel && (
        <div className="mt-2">
          <div className="flex items-center justify-between text-[10px] text-slate-500">
            <span className="flex items-center gap-1">
              {tgt && <TargetIcon className="w-2.5 h-2.5" />}
              <span className="tabular">{progressLabel}</span>
            </span>
            {progressPct != null && <span className="tabular">{progressPct}%</span>}
          </div>
          {progressPct != null && (
            <div className="w-full h-1 bg-slate-200 rounded-sm mt-1 overflow-hidden">
              <div
                className="h-full"
                style={{
                  width: `${progressPct}%`,
                  background: progressPct >= 100 ? "#10B981" : progressPct >= 75 ? "#10B981" : "#F59E0B",
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const nodeTypes = { logframe: LogFrameNode };

export default function LogFrameDiagram({ indicators, breakdownByIid = {} }) {
  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => layoutIndicators(indicators, breakdownByIid),
    [indicators, breakdownByIid],
  );
  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);

  // Lane headers drawn as a fixed left column overlay
  const lanes = useMemo(() => {
    const present = new Set(indicators.map((i) => LEVEL_COLORS[levelFor(i)].lane));
    return LEVEL_ORDER.filter((l) => present.has(l));
  }, [indicators]);

  if (indicators.length === 0) {
    return (
      <div className="border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="logframe-diagram-empty">
        Add at least one indicator to see the LogFrame diagram.
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden relative" data-testid="logframe-diagram">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <p className="label-overline">Logical framework — visual diagram</p>
        <div className="flex items-center gap-3 text-[10px]">
          {lanes.map((l) => {
            const c = Object.values(LEVEL_COLORS).find((x) => x.lane === l) || LEVEL_COLORS.Default;
            return (
              <span key={l} className="flex items-center gap-1.5 uppercase tracking-wider" style={{ color: c.border }}>
                <span className="inline-block w-2 h-2 rounded-sm" style={{ background: c.border }} />
                {l}
              </span>
            );
          })}
        </div>
      </div>
      <div style={{ height: Math.max(360, Math.min(700, 120 + indicators.length * 60)) }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.18 }}
          proOptions={{ hideAttribution: true }}
          nodesDraggable
          nodesConnectable={false}
          edgesFocusable={false}
          minZoom={0.4}
          maxZoom={1.6}
        >
          <Background color="#E2E8F0" gap={24} />
          <MiniMap pannable zoomable nodeColor={(n) => LEVEL_COLORS[levelFor(n.data.indicator)].border} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
    </div>
  );
}
